import { useEffect, useMemo, useRef } from 'react';
import { UseDataFetchingProps, ApiResponse } from '../types/index';
import { CACHE_DURATION, MAX_CACHE_ENTRIES, FETCH_DEBOUNCE_DELAY } from '../constants/index';
import buildResultEndpoint from '../../../utils/buildResultEndpoint';
import fetchEndpoint from '../../../services/fetchEndpoint';

export const useDataFetching = ({
  view,
  source,
  tab,
  searchItems,
  isEvidence,
  isMapping,
  skip,
  limit,
  orderByParam,
  order,
  defaultFilterValue,
  sourceWeights,
  userContext,
  mappingContent,
  checkIncompleteFilters,
  setRowData,
  setTotalCount,
  setTotalResults,
  setLoading,
  setIsSessionExpired,
  setShowPopup,
  setIncompleteFilterMessage,
  cacheRef,
  prevDataRef,
  fetchTimeoutRef,
}: UseDataFetchingProps) => {
  const {
    isAuthenticated,
    authorization,
    setAuthorization,
    setIsAuthenticated,
    setGroups,
    setAuthError,
    restrictGridResultCount,
    restrictBrowseAll,
    groups,
  } = userContext;

  const isTrial = groups?.includes('ROLE_TRIAL');

  const filterValuesKey = useMemo(
    () =>
      JSON.stringify(
        defaultFilterValue
          .filter(f => f.value)
          .map(f => ({ name: f.name, value: f.value, operator: f.operator })),
      ),
    [defaultFilterValue],
  );

  const paramsRef = useRef({
    view,
    source,
    tab,
    searchItems,
    skip,
    limit,
    orderByParam,
    order,
    defaultFilterValue,
    sourceWeights,
    isEvidence,
    isMapping,
    mappingContent,
    isAuthenticated,
    authorization,
    restrictGridResultCount,
    restrictBrowseAll,
    isTrial,
    checkIncompleteFilters,
    setRowData,
    setTotalCount,
    setTotalResults,
    setLoading,
    setIsSessionExpired,
    setShowPopup,
    setIncompleteFilterMessage,
    cacheRef,
    prevDataRef,
  });
  paramsRef.current = {
    view,
    source,
    tab,
    searchItems,
    skip,
    limit,
    orderByParam,
    order,
    defaultFilterValue,
    sourceWeights,
    isEvidence,
    isMapping,
    mappingContent,
    isAuthenticated,
    authorization,
    restrictGridResultCount,
    restrictBrowseAll,
    isTrial,
    checkIncompleteFilters,
    setRowData,
    setTotalCount,
    setTotalResults,
    setLoading,
    setIsSessionExpired,
    setShowPopup,
    setIncompleteFilterMessage,
    cacheRef,
    prevDataRef,
  };

  useEffect(() => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = null;
      setLoading(false);
    }

    fetchTimeoutRef.current = setTimeout(() => {
      const p = paramsRef.current;
      if (!(p.view && p.source && p.tab && p.searchItems)) {
        p.setLoading(false);
        return;
      }

      const incompleteMessage = p.checkIncompleteFilters(p.defaultFilterValue);
      p.setIncompleteFilterMessage(incompleteMessage);

      if (incompleteMessage) {
        p.setRowData([]);
        p.setTotalCount(0);
        p.setTotalResults(0);
        p.prevDataRef.current = { data: [], count: 0 };
        p.setLoading(false);
        return;
      }

      let urlEndpoint = buildResultEndpoint({
        view: p.view,
        source: p.source,
        tab: p.tab,
        searchItems: p.searchItems,
        isEvidence: p.isEvidence,
        isMapping: p.isMapping,
        mappingContent: p.mappingContent,
        pageNum:
          p.restrictGridResultCount || (p.restrictBrowseAll && p.view === 'ALL') ? 0 : p.skip / p.limit,
        pageSize: p.limit,
        orderByParam: p.orderByParam,
        order: p.order,
        defaultFilterValue: p.defaultFilterValue as any,
        sourceWeights: p.sourceWeights,
      });

      if (!p.isAuthenticated && p.skip !== 0) {
        p.setShowPopup(true);
        p.setLoading(false);
        return;
      }

      if (p.restrictBrowseAll && p.view === 'ALL' && p.skip !== 0) {
        p.setShowPopup(true);
        p.setLoading(false);
        return;
      }

      if (p.isTrial && p.skip !== 0) {
        p.setShowPopup(true);
        p.setLoading(false);
        return;
      }

      if (!urlEndpoint) {
        p.setLoading(false);
        return;
      }
      urlEndpoint = !p.isAuthenticated ? `${urlEndpoint}&skey=unauth` : urlEndpoint;

      const cacheKey = urlEndpoint;
      if (p.cacheRef.current[cacheKey]) {
        const cached = p.cacheRef.current[cacheKey];
        const isRecentCache = Date.now() - cached.timestamp < CACHE_DURATION;

        if (isRecentCache) {
          p.setRowData(cached.data);
          p.setTotalCount(cached.totalCount);
          p.setTotalResults(cached.totalCount);
          p.setIsSessionExpired(false);
          p.prevDataRef.current = { data: cached.data, count: cached.totalCount };
          p.setLoading(false);
          return;
        } else {
          delete p.cacheRef.current[cacheKey];
        }
      }

      p.setLoading(true);
      (fetchEndpoint as any)(
        urlEndpoint,
        p.authorization,
        setAuthorization,
        setIsAuthenticated,
        setGroups,
        setAuthError,
      )
        .then((res: ApiResponse) => {
          const data = res?.payload;
          const totalElements = res?.paging ? res.paging.totalElements : 0;
          const p2 = paramsRef.current;

          if (data) {
            let totalCountValue = parseInt(totalElements.toString());

            const hasSpecificSearchItems =
              p2.searchItems &&
              p2.searchItems.length > 0 &&
              !p2.searchItems.some((item: any) => item.id === 'ALL');

            const urlParams = new URLSearchParams(window.location.search);
            const identsParam = urlParams.get('idents');
            const isFromAdvancedSearch = identsParam && identsParam !== 'ALL' && identsParam.includes('-');

            if (hasSpecificSearchItems || isFromAdvancedSearch) {
              const filteredCount =
                res?.paging?.filteredElements || res?.paging?.filteredCount || res?.filteredCount;

              if (filteredCount && filteredCount !== totalCountValue && filteredCount < totalCountValue) {
                totalCountValue = parseInt(filteredCount.toString());
              } else {
                if (data.length === 0) {
                  totalCountValue = 0;
                } else if (p2.skip === 0 && data.length < p2.limit) {
                  totalCountValue = data.length;
                } else {
                  const isUnreasonablyHigh = totalCountValue > 100000;

                  if (isUnreasonablyHigh) {
                    if (data.length === p2.limit) {
                      totalCountValue = Math.min(data.length * 50, 10000);
                    } else {
                      totalCountValue = p2.skip + data.length;
                    }
                  }
                }
              }
            }

            p2.setRowData(data);
            p2.setTotalCount(totalCountValue);
            p2.setTotalResults(totalCountValue);
            p2.setIsSessionExpired(false);

            p2.prevDataRef.current = { data, count: totalCountValue };

            p2.cacheRef.current[cacheKey] = {
              data: data,
              totalCount: totalCountValue,
              timestamp: Date.now(),
            };

            const cacheKeys = Object.keys(p2.cacheRef.current);
            if (cacheKeys.length > MAX_CACHE_ENTRIES) {
              const sortedKeys = cacheKeys.sort(
                (a, b) => p2.cacheRef.current[a].timestamp - p2.cacheRef.current[b].timestamp,
              );
              sortedKeys.slice(0, cacheKeys.length - MAX_CACHE_ENTRIES).forEach(key => {
                delete p2.cacheRef.current[key];
              });
            }
          } else if (res?.status === 403) {
            p2.setIsSessionExpired(true);
            p2.setRowData([]);
            p2.setTotalCount(0);
            p2.prevDataRef.current = { data: [], count: 0 };
          } else {
            p2.setRowData([]);
            p2.setTotalCount(0);
            p2.prevDataRef.current = { data: [], count: 0 };
          }
        })
        .catch(() => {
          paramsRef.current.setRowData([]);
          paramsRef.current.setTotalCount(0);
        })
        .finally(() => {
          paramsRef.current.setLoading(false);
        });
    }, FETCH_DEBOUNCE_DELAY);

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
    };
  }, [
    view,
    source,
    tab,
    searchItems,
    skip,
    limit,
    order,
    orderByParam,
    filterValuesKey,
    sourceWeights,
    isEvidence,
    isMapping,
  ]);

  return {
    filterValuesKey,
  };
};
